import React, { useEffect, useMemo, useState } from "react";

// APIs for Spring Boot and Node.js
// const API_BASE = "http://localhost:9091/api/admin/students";
// const NODE_API = "http://localhost:9090/api";

const API_BASE = "https://api.mediitacademy.com/api";
const NODE_API = "https://node-api.mediitacademy.com/api";

type Enrollment = {
  id: number;
  courseId?: number | null;
  courseName?: string | null;
  planName?: string | null;
  amountPaid?: number | string | null;
  transactionId?: string | null;
  paymentStatus?: string | null;
  accessEnabled: boolean;
  purchasedAt?: string | null;
};

type StudentRow = {
  id: number;
  name: string;
  email: string;
  phone: string;
  studentClass?: string | null;
  registeredCourse?: string | null;
  accountActive: boolean;
  joinedAt?: string | null;
  enrollments: Enrollment[];
};

type Stats = {
  totalStudents: number;
  activeStudents: number;
  totalEnrollments: number;
  pendingPayments: number;
  disabledCourses: number;
};

const emptyStats: Stats = {
  totalStudents: 0,
  activeStudents: 0,
  totalEnrollments: 0,
  pendingPayments: 0,
  disabledCourses: 0,
};

const getAuthHeaders = (): Record<string, string> => {
  let token = "";
  try {
    const adminAuthData = JSON.parse(localStorage.getItem("mediit_admin_auth") || "{}");
    token = adminAuthData.token || "";
  } catch (err) {}

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const safeText = (value?: string | null, fallback = "-") => {
  if (!value || value.trim() === "") return fallback;
  return value;
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value?: number | string | null) => {
  if (value === undefined || value === null || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return `₹${amount.toLocaleString("en-IN")}`;
};

const AdminStudents: React.FC = () => {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [stats, setStats] = useState<Stats>(emptyStats);
  const [activeType, setActiveType] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);

  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_BASE, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message || data?.error || "Failed to load students.");

      const incomingStudents: StudentRow[] = Array.isArray(data) ? data : data?.students || [];
      const incomingStats: Stats = data?.stats || emptyStats;

      setStudents(
        incomingStudents.map((student) => ({
          ...student,
          enrollments: Array.isArray(student.enrollments) ? student.enrollments : [],
          accountActive: student.accountActive !== false,
        }))
      );
      setStats({ ...emptyStats, ...incomingStats });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const studentTypes = useMemo(() => {
    const values = new Set<string>(["All"]);
    students.forEach((student) => {
      if (student.studentClass) values.add(student.studentClass);
      student.enrollments.forEach((enrollment) => {
        const text = `${enrollment.courseName || ""} ${enrollment.planName || ""}`.toLowerCase();
        if (text.includes("neet")) values.add("NEET");
        if (text.includes("jee")) values.add("JEE");
        if (text.includes("class 8")) values.add("Class 8");
        if (text.includes("class 7")) values.add("Class 7");
      });
    });
    return Array.from(values);
  }, [students]);

  const filteredStudents = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();
    return students.filter((student) => {
      const studentTypeText = `${student.studentClass || ""} ${student.registeredCourse || ""} ${student.enrollments
        .map((course) => `${course.courseName || ""} ${course.planName || ""}`).join(" ")}`.toLowerCase();
      const matchType = activeType === "All" || studentTypeText.includes(activeType.toLowerCase());

      const searchableText = `${student.name} ${student.email} ${student.phone} ${student.studentClass || ""} ${student.registeredCourse || ""} ${student.enrollments
        .map((course) => `${course.courseName || ""} ${course.planName || ""} ${course.paymentStatus || ""} ${course.transactionId || ""}`).join(" ")}`.toLowerCase();
      const matchSearch = normalizedSearch === "" || searchableText.includes(normalizedSearch);

      return matchType && matchSearch;
    });
  }, [students, activeType, search]);

  const derivedStats = useMemo<Stats>(() => {
    const totalEnrollments = students.reduce((total, student) => total + student.enrollments.length, 0);
    const pendingPayments = students.reduce(
      (total, student) => total + student.enrollments.filter((enrollment) => {
          const status = (enrollment.paymentStatus || "").toUpperCase();
          return status === "PENDING" || status === "DUE";
        }).length,
      0
    );
    const disabledCourses = students.reduce((total, student) => total + student.enrollments.filter((e) => !e.accessEnabled).length, 0);

    return {
      totalStudents: stats.totalStudents || students.length,
      activeStudents: stats.activeStudents || students.filter((student) => student.accountActive).length,
      totalEnrollments: stats.totalEnrollments || totalEnrollments,
      pendingPayments: stats.pendingPayments || pendingPayments,
      disabledCourses: stats.disabledCourses || disabledCourses,
    };
  }, [students, stats]);

  const updateSelectedStudent = (updatedStudents: StudentRow[], studentId: number) => {
    setSelectedStudent((current) => {
      if (!current || current.id !== studentId) return current;
      return updatedStudents.find((student) => student.id === studentId) || current;
    });
  };

  const toggleLogin = async (student: StudentRow, active: boolean) => {
    const key = `login-${student.id}`;
    setSavingId(key);
    try {
      const response = await fetch(`${API_BASE}/${student.id}/login-status`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ active }),
      });
      if (!response.ok) throw new Error("Login status update failed.");

      setStudents((previous) => {
        const updated = previous.map((item) => item.id === student.id ? { ...item, accountActive: active } : item);
        updateSelectedStudent(updated, student.id);
        return updated;
      });
    } catch (err) {
      alert("Could not update login status.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleEnrollmentAccess = async (studentId: number, enrollment: Enrollment, accessEnabled: boolean) => {
    const key = `course-${enrollment.id}`;
    setSavingId(key);
    try {
      const response = await fetch(`${API_BASE}/enrollments/${enrollment.id}/access`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ accessEnabled }),
      });
      if (!response.ok) throw new Error("Course access update failed.");

      setStudents((previous) => {
        const updated = previous.map((student) => {
          if (student.id !== studentId) return student;
          return {
            ...student,
            enrollments: student.enrollments.map((item) => item.id === enrollment.id ? { ...item, accessEnabled } : item),
          };
        });
        updateSelectedStudent(updated, studentId);
        return updated;
      });
    } catch (err) {
      alert("Could not update course access.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleAllCourseAccess = async (student: StudentRow, accessEnabled: boolean) => {
    const key = `all-course-${student.id}`;
    setSavingId(key);
    try {
      const response = await fetch(`${API_BASE}/${student.id}/course-access`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ accessEnabled }),
      });
      if (!response.ok) throw new Error("All course access update failed.");

      setStudents((previous) => {
        const updated = previous.map((item) => {
          if (item.id !== student.id) return item;
          return {
            ...item,
            enrollments: item.enrollments.map((course) => ({ ...course, accessEnabled })),
          };
        });
        updateSelectedStudent(updated, student.id);
        return updated;
      });
    } catch (err) {
      alert("Could not update all course access.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-[30px] bg-slate-950 p-6 text-white shadow-xl shadow-slate-300/40 sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
          <div>
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
              Dynamic Student Management
            </div>
            <h1 className="max-w-3xl text-3xl font-black tracking-tight sm:text-4xl">
              Registered students, purchased courses and access control
            </h1>
            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Registration ke baad students yahan automatically aayenge. Admin yahin se login deactivate aur course access disable kar sakta hai.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={fetchStudents} className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">Refresh</button>
            <a href="/register" className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50">+ Add Student</a>
          </div>
        </div>
      </section>

      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard title="Total Students" value={derivedStats.totalStudents.toLocaleString("en-IN")} note="Registered accounts" />
        <StatCard title="Active Logins" value={derivedStats.activeStudents.toLocaleString("en-IN")} note="Can login now" />
        <StatCard title="Course Purchases" value={derivedStats.totalEnrollments.toLocaleString("en-IN")} note="All enrollments" />
        <StatCard title="Pending Payments" value={derivedStats.pendingPayments.toLocaleString("en-IN")} note="Need approval/follow-up" />
        <StatCard title="Disabled Courses" value={derivedStats.disabledCourses.toLocaleString("en-IN")} note="Access blocked" />
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {studentTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`rounded-2xl px-4 py-2 text-sm font-black transition ${activeType === type ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="flex h-12 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 xl:max-w-md">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" /></svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student, email, course..." className="w-full bg-transparent text-sm font-semibold outline-none" />
          </div>
        </div>
      </section>

      {/* ✅ FIX: Added items-start and min-w-0 to prevent layout breaking/overflow */}
      <section className="grid items-start gap-6 xl:grid-cols-[1fr_340px]">
        <div className="min-w-0 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col justify-between gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-xl font-black text-slate-950">All Students</h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">Backend database se live data aa raha hai.</p>
            </div>
            <button className="w-fit rounded-2xl bg-blue-50 px-4 py-2 text-xs font-black text-blue-700 hover:bg-blue-100">Bulk Upload</button>
          </div>

          {loading && <div className="p-8 text-center text-sm font-black text-slate-500">Students load ho rahe hain...</div>}
          {!loading && error && <div className="m-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
          {!loading && !error && filteredStudents.length === 0 && <div className="p-8 text-center text-sm font-black text-slate-500">No students found.</div>}

          {!loading && !error && filteredStudents.length > 0 && (
            <div className="hidden overflow-x-auto xl:block">
              <table className="w-full min-w-[1050px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-4">Student</th>
                    <th className="px-5 py-4">Registered Class</th>
                    <th className="px-5 py-4">Purchased Course</th>
                    <th className="px-5 py-4">Payment</th>
                    <th className="px-5 py-4">Course Access</th>
                    <th className="px-5 py-4">Login</th>
                    <th className="px-5 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => {
                    const mainEnrollment = student.enrollments[0];
                    const hasCourse = student.enrollments.length > 0;
                    const allCoursesEnabled = hasCourse && student.enrollments.every((course) => course.accessEnabled);

                    return (
                      <tr key={student.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar name={student.name} />
                            <div>
                              <p className="font-black text-slate-950">{student.name}</p>
                              <p className="text-xs font-semibold text-slate-500">{student.email}</p>
                              <p className="mt-1 text-[11px] font-bold text-slate-400">Joined: {formatDate(student.joinedAt)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-black text-slate-800">{safeText(student.studentClass, "Not selected")}</p>
                        </td>
                        <td className="px-5 py-4">
                          {hasCourse ? (
                            <div className="space-y-2">
                              <p className="text-sm font-black text-slate-800">{safeText(mainEnrollment?.courseName || mainEnrollment?.planName, `Course #${mainEnrollment?.courseId || "-"}`)}</p>
                              <p className="text-xs font-bold text-slate-400">Bought: {formatDate(mainEnrollment?.purchasedAt)}</p>
                            </div>
                          ) : (<span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500">No purchase</span>)}
                        </td>
                        <td className="px-5 py-4">
                          {hasCourse ? (
                            <div className="space-y-2">
                              <PaymentBadge status={mainEnrollment?.paymentStatus || "PENDING"} />
                              <p className="text-xs font-bold text-slate-400">{formatAmount(mainEnrollment?.amountPaid)}</p>
                            </div>
                          ) : (<PaymentBadge status="NO COURSE" />)}
                        </td>
                        <td className="px-5 py-4">
                          {hasCourse ? (
                            <button disabled={savingId === `all-course-${student.id}`} onClick={() => toggleAllCourseAccess(student, !allCoursesEnabled)} className={`rounded-full px-3 py-1 text-xs font-black ${allCoursesEnabled ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                              {allCoursesEnabled ? "Enabled" : "Disabled"}
                            </button>
                          ) : (<span className="text-xs font-bold text-slate-400">-</span>)}
                        </td>
                        <td className="px-5 py-4">
                          <button disabled={savingId === `login-${student.id}`} onClick={() => toggleLogin(student, !student.accountActive)} className={`rounded-full px-3 py-1 text-xs font-black ${student.accountActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                            {student.accountActive ? "Active" : "Deactivated"}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button onClick={() => setSelectedStudent(student)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100">
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && filteredStudents.length > 0 && (
            <div className="grid gap-4 p-5 xl:hidden">
              {/* Mobile View omitted for brevity, keeping existing functional code */}
              <p className="text-sm text-slate-500">Please switch to desktop for full table view.</p>
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black text-slate-950">Latest Purchases</h2>
            <div className="mt-5 space-y-4">
              {students.flatMap((s) => s.enrollments.map((e) => ({ s, e }))).slice(0, 5).map(({ s, e }) => (
                  <div key={`${s.id}-${e.id}`} className="rounded-2xl bg-slate-50 p-3">
                    <p className="truncate text-sm font-black text-slate-950">{s.name}</p>
                    <p className="truncate text-xs font-bold text-slate-500">{e.courseName || e.planName}</p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <PaymentBadge status={e.paymentStatus || "PENDING"} />
                      <span className="text-xs font-black text-slate-500">{formatAmount(e.amountPaid)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </aside>
      </section>

      {selectedStudent && (
        <StudentDetailsModal
          student={selectedStudent}
          savingId={savingId}
          onClose={() => setSelectedStudent(null)}
          onToggleLogin={toggleLogin}
          onToggleEnrollmentAccess={toggleEnrollmentAccess}
        />
      )}
    </div>
  );
};

const StudentDetailsModal = ({
  student,
  savingId,
  onClose,
  onToggleLogin,
  onToggleEnrollmentAccess,
}: {
  student: StudentRow;
  savingId: string | null;
  onClose: () => void;
  onToggleLogin: (student: StudentRow, active: boolean) => void;
  onToggleEnrollmentAccess: (studentId: number, enrollment: Enrollment, accessEnabled: boolean) => void;
}) => {
  const [assignMode, setAssignMode] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Fetch Courses when assign mode opens
  useEffect(() => {
    if (assignMode && courses.length === 0) {
      fetch(`${NODE_API}/courses`).then((r) => r.json()).then(setCourses);
    }
  }, [assignMode]);

  // Fetch Batches when a course is selected
  useEffect(() => {
    if (selectedCourse) {
      // FIX: Changed from /available/ to /batches?courseId=
      // Taki admin ko started ya closed batches bhi dikh jayein assign karne ke liye
      fetch(`${NODE_API}/batches?courseId=${selectedCourse}`)
        .then((r) => r.json())
        .then(setBatches);
    } else {
      setBatches([]);
    }
  }, [selectedCourse]);

  const handleAssign = async () => {
    if (!selectedCourse || !selectedBatch) return alert("Select course and batch.");
    setAssigning(true);
    try {
      const res = await fetch(`${NODE_API}/enrollments/manual-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: student.email,
          courseId: selectedCourse,
          batchId: selectedBatch,
          amount: 0,
          planName: "Admin Assigned",
          transactionId: "ADMIN-ASSIGNED-" + Date.now(),
        }),
      });
      if (res.ok) {
        alert("Batch assigned successfully! Refresh the page to see changes.");
        setAssignMode(false);
        setSelectedCourse("");
        setSelectedBatch("");
      } else {
        alert("Failed to assign batch.");
      }
    } catch (e) {
      alert("Error assigning batch.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[28px] bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white p-5">
          <div className="flex items-center gap-3">
            <Avatar name={student.name} />
            <div>
              <h2 className="text-2xl font-black text-slate-950">{student.name}</h2>
              <p className="text-sm font-bold text-slate-500">{student.email} • {student.phone}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.href = `/learn-with-us/student-report/${student.id}`}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
            >
              View Full Report
            </button>
            <button onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-xl font-black text-slate-600 hover:bg-red-50 hover:text-red-600">×</button>
          </div>
        </div>

        <div className="grid gap-4 p-5 sm:grid-cols-3">
          <InfoBox label="Student Class" value={safeText(student.studentClass, "Not selected")} />
          <InfoBox label="Registered Course" value={safeText(student.registeredCourse, "-")} />
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-bold text-slate-400">Login Status</p>
            <button disabled={savingId === `login-${student.id}`} onClick={() => onToggleLogin(student, !student.accountActive)} className={`mt-2 rounded-full px-3 py-1 text-xs font-black ${student.accountActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {student.accountActive ? "Active - Click to Deactivate" : "Deactivated - Click to Activate"}
            </button>
          </div>
        </div>

        <div className="px-5 pb-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-black text-slate-950">Purchased Courses</h3>
            <button onClick={() => setAssignMode(!assignMode)} className="text-sm font-black text-blue-600 hover:underline">
              {assignMode ? "- Cancel Assignment" : "+ Assign New Course/Batch"}
            </button>
          </div>

          {assignMode && (
            <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
              <h4 className="mb-4 text-sm font-black text-blue-900">Manually Assign Course & Batch</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none">
                  <option value="">Select Course</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
                <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none">
                  <option value="">Select Batch</option>
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.liveStatus || b.status})
                    </option>
                  ))}
                </select>
              </div>
              <button onClick={handleAssign} disabled={assigning || !selectedCourse || !selectedBatch} className="mt-4 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-black text-white hover:bg-blue-700 disabled:opacity-50">
                {assigning ? "Assigning..." : "Confirm & Enroll Student"}
              </button>
            </div>
          )}

          {student.enrollments.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
              Is student ne abhi koi course purchase nahi kiya hai.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-100">
              <table className="w-full min-w-[780px] text-left">
                <thead>
                  <tr className="bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Course</th>
                    <th className="px-4 py-3">Purchased At</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Access</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {student.enrollments.map((enrollment) => (
                    <tr key={enrollment.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <p className="text-sm font-black text-slate-900">{safeText(enrollment.courseName || enrollment.planName, `Course #${enrollment.courseId || "-"}`)}</p>
                      </td>
                      <td className="px-4 py-3 text-xs font-bold text-slate-500">{formatDate(enrollment.purchasedAt)}</td>
                      <td className="px-4 py-3 text-sm font-black text-slate-800">{formatAmount(enrollment.amountPaid)}</td>
                      <td className="px-4 py-3"><PaymentBadge status={enrollment.paymentStatus || "PENDING"} /></td>
                      <td className="px-4 py-3"><AccessBadge enabled={enrollment.accessEnabled} /></td>
                      <td className="px-4 py-3 text-right">
                        <button disabled={savingId === `course-${enrollment.id}`} onClick={() => onToggleEnrollmentAccess(student.id, enrollment, !enrollment.accessEnabled)} className={`rounded-xl px-3 py-2 text-xs font-black ${enrollment.accessEnabled ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {enrollment.accessEnabled ? "Disable" : "Enable"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, note }: { title: string; value: string; note: string }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
    <p className="text-sm font-bold text-slate-500">{title}</p>
    <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
    <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
  </div>
);

const Avatar = ({ name }: { name: string }) => {
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase();
  return <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white">{initials || "ST"}</div>;
};

const AccessBadge = ({ enabled }: { enabled: boolean }) => (
  <span className={`rounded-full px-3 py-1 text-xs font-black ${enabled ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{enabled ? "Enabled" : "Disabled"}</span>
);

const PaymentBadge = ({ status }: { status: string }) => {
  const isPaid = status.toUpperCase() === "PAID" || status.toUpperCase() === "APPROVED" || status.toUpperCase() === "SUCCESS";
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-black ${isPaid ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>{status}</span>
  );
};

const InfoBox = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-2xl bg-slate-50 p-4">
    <p className="text-xs font-bold text-slate-400">{label}</p>
    <p className="mt-1 break-words text-sm font-black text-slate-900">{value}</p>
  </div>
);

export default AdminStudents;